import { useMemo, useState, useEffect } from "react";
import { getHrDirectory, type HrDirectory, type HrEmployee } from "../api";
import type { LetterFormField, LetterFormPayload, LetterFormSpec } from "../sse";

/**
 * The all-in-one letter intake card (SSE `form` event). One form replaces
 * turn-by-turn field collection: type selector, HR-roster employee picker
 * with one provenance line, prefill flags (every synced value stays editable
 * — an edited value wins), a single column of grouped fields with stem-rule
 * guidance on focus, optional fields behind one disclosure, and client-side
 * required/number validation. Submit sends `form_payload` on the next ask;
 * the clerk closes out through the pipeline.
 */

/** Signatory memory — the last submitted signatory, restored on every form. */
const SIGNATORY_KEY = "nexus-letter-signatory";

/** Where a field's current value came from — drives the field flag.
 *  Any user edit moves the field to "user" and the flag disappears. */
type FieldSource = "user" | "hr" | "chat" | "remembered";

interface FieldState {
  value: string;
  source: FieldSource;
  /** A value the conversation supplied as a record rather than as text — the
   *  prior-warnings shape the letter composer turns into prose. It is held as
   *  it arrived and submitted untouched; the field shows it for review and
   *  offers to replace it with the reader's own wording. */
  record?: unknown;
}

/** Text the field can edit. Anything else is a record. */
function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

/** An ISO date read as a date. Display only — a value is submitted as it
 *  arrived. */
function readableValue(v: string | number | boolean): string {
  const s = String(v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? s
    : d.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
}

/** A record read back for review: its own values, in the order given. The
 *  wording of the sentence the letter carries is the composer's to decide, so
 *  this states what was captured and never drafts prose for it. */
function describeRecord(v: unknown): string[] {
  if (Array.isArray(v)) return v.flatMap(describeRecord);
  if (isScalar(v)) return [readableValue(v)];
  if (v && typeof v === "object") {
    const parts = Object.values(v as Record<string, unknown>)
      .filter(isScalar).map(readableValue).filter(s => s.trim() !== "");
    return parts.length ? [parts.join(" · ")] : [];
  }
  return [];
}

/** HR-directory prefill per payload field name (the plan's prefill map). */
const HR_PREFILL: Record<string, (e: HrEmployee, orgName: string | null) => string | null> = {
  company_name: (_e, orgName) => orgName,
  employee_name: (e) => e.display_name,
  employment_type: (e) => humanEmploymentType(e.employment_type),
  current_salary: (e) => (e.current_salary_sgd === null ? null : String(e.current_salary_sgd)),
  position: (e) => e.job_title,
  job_title: (e) => e.job_title,
};

function humanEmploymentType(t: string | null): string | null {
  if (!t) return null;
  const map: Record<string, string> = {
    full_time: "Full-time", part_time: "Part-time", contract: "Contract",
  };
  return map[t] || t.replace(/_/g, " ");
}

/** Mid-sentence template params: guidance shown while the field has focus, and
 *  a lint that catches values restarting their sentence stem before they ever
 *  reach the template. */
const STEM_RULES: Record<string, { hint: string; bad: RegExp; warn: string }> = {
  expected_correction: {
    hint: 'Continues "You are expected to…" — e.g. "be present at all rostered shifts".',
    bad: /^\s*(you are (expected|required) to)\b/i,
    warn: 'The letter already says "You are expected to" — start at the verb.',
  },
  incident_details: {
    hint: 'Continues "It has come to our attention that…" — e.g. "you were absent on three rostered shifts".',
    bad: /^\s*it has come to our attention\b/i,
    warn: 'The letter already says "It has come to our attention that" — start after it.',
  },
  termination_reason: {
    hint: 'Continues "The reason for the termination is…" — state the reason itself.',
    bad: /^\s*the reason for (the )?termination\b/i,
    warn: "The letter already carries the stem — state the reason itself.",
  },
  property_return: {
    hint: 'Continues "You are required to return…" — e.g. "the company laptop and access card".',
    bad: /^\s*you are required to return\b/i,
    warn: 'The letter already says "You are required to return" — start after it.',
  },
  re_subject: {
    hint: 'The subject after "RE: Written Warning –".',
    bad: /^\s*written warning\b/i,
    warn: 'The RE: line already carries "Written Warning" — give the subject only.',
  },
};

/** User-facing field guidance. A placeholder is only ever the short literal a
 *  blank field falls back to; anything longer is a hint, shown on focus, so no
 *  guidance can be clipped by an input's width. An empty hint is deliberate:
 *  the placeholder already says it, so the field stays silent. */
const FIELD_HELP: Record<string, { hint?: string; placeholder?: string }> = {
  re_subject: { placeholder: "the policy breached" },
  response_period: { hint: "", placeholder: "five (5) working days" },
  review_period: { hint: "", placeholder: "three (3) months" },
  notice_period: { hint: "Sets the standard notice sentence; blank leaves it out.", placeholder: "one (1) month" },
  employment_type: { hint: "", placeholder: "Full-time (permanent)" },
  date: { hint: "" },
  effective_date: { hint: "" },
  prior_warnings: { hint: "Blank means this is the first written warning." },
  notice_details: { hint: "A full sentence that replaces the standard notice line." },
  final_pay_details: { hint: "An amount, or a sentence; blank uses the standard settlement line." },
  benefits_info: { hint: "Blank uses the standard continuing-benefits line." },
  benefits: { hint: "Blank keeps existing terms." },
  current_salary: { hint: "A plain monthly amount — the letter formats it.", placeholder: "e.g. 4800" },
  new_salary: { hint: "A plain monthly amount — the letter formats it.", placeholder: "e.g. 5200" },
};

/** Display grouping. Fields not named here land in "Letter details". */
const GROUPS: Array<{ label: string; fields: string[] }> = [
  { label: "Company", fields: ["company_name", "company_address", "company_phone"] },
  { label: "Employee", fields: ["employee_name", "employee_address", "employment_type", "position", "job_title"] },
  { label: "Letter details", fields: [] },
  { label: "Signatory & date", fields: ["signatory_name", "signatory_title", "date"] },
];

const FIELD_LABELS: Record<string, string> = {
  company_name: "Company name",
  company_address: "Company address",
  company_phone: "Company phone",
  employee_name: "Employee name",
  employee_address: "Employee address",
  employment_type: "Employment type",
  incident_date: "Incident date",
  incident_details: "What happened",
  policy_violated: "Policy violated",
  expected_correction: "Expected correction",
  grievance_contact: "Grievance contact",
  prior_warnings: "Prior warnings",
  response_period: "Response period",
  review_period: "Review period",
  re_subject: "RE: subject",
  termination_date: "Termination date",
  termination_reason: "Termination reason",
  notice_period: "Notice period",
  notice_details: "Notice details",
  final_pay_details: "Final pay details",
  benefits_info: "Benefits info",
  property_return: "Property to return",
  current_salary: "Current salary (S$/month)",
  new_salary: "New salary (S$/month)",
  effective_date: "Effective date",
  new_position: "New position",
  benefits: "Benefits",
  signatory_name: "Signatory name",
  signatory_title: "Signatory title",
  date: "Letter date",
};

function labelOf(name: string): string {
  return FIELD_LABELS[name] || name.replace(/_/g, " ").replace(/^./, c => c.toUpperCase());
}

/** Pipeline names carry the product prefix ("HR AI — Written Warning Letter");
 *  the card shows the letter itself. */
function typeLabel(name: string): string {
  const m = /^[^—–]{1,32}[—–]\s*(.+)$/.exec(name.trim());
  return m ? m[1] : name;
}

/** Field guidance falling back to the pipeline's own schema description. That
 *  text is written for the clerk, so it is rewritten for a reader: the
 *  redundant "optional" prefix goes, and any payload field name it mentions
 *  becomes that field's label — a raw identifier never reaches the page. */
function humanizeDescription(desc: string, names: Set<string>): string {
  let out = desc.replace(/^\s*optional\b[\s,—–-]*/i, "");
  out = out.replace(/[a-z][a-z0-9]*(?:_[a-z0-9]+)+/g, tok => (
    names.has(tok) ? `the ${labelOf(tok).toLowerCase()}` : tok.replace(/_/g, " ")
  ));
  out = out.trim();
  if (!out) return "";
  out = out[0].toUpperCase() + out.slice(1);
  return /[.!?]$/.test(out) ? out : `${out}.`;
}

/** The HR systems the directory syncs from, named as their vendors. */
const HR_SOURCES: Record<string, string> = {
  employment_hero: "Employment Hero",
  zoho_people: "Zoho People",
};

function sourceLabel(source: string | null): string {
  if (!source) return "your HR system";
  return HR_SOURCES[source]
    || source.replace(/_/g, " ").replace(/\b[a-z]/g, c => c.toUpperCase());
}

const TODAY_FIELDS = new Set(["effective_date", "date"]);

/** A date field the browser can present in the reader's own format. The value
 *  it holds stays the ISO string the payload carries; a value the clerk sent
 *  in prose keeps a text input so nothing the conversation gave is dropped. */
function isDateField(f: LetterFormField, value: string): boolean {
  return f.kind === "string"
    && /(^|_)date$/.test(f.name)
    && /^(\d{4}-\d{2}-\d{2})?$/.test(value);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A box tall enough to read its own value. What the conversation carried in
 *  arrives whole, so the reader approves the wording a letter will actually
 *  carry rather than the first two lines of it. */
function rowsFor(value: string): number {
  const lines = value.split("\n")
    .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 44)), 0);
  return Math.min(10, Math.max(2, lines));
}

/** Initial field state for one letter type: clerk-known values first, then
 *  the remembered signatory, then today's date for date-of-effect fields. */
function initialFields(fields: LetterFormField[]): Record<string, FieldState> {
  let signatory: { name?: string; title?: string } = {};
  try { signatory = JSON.parse(localStorage.getItem(SIGNATORY_KEY) || "{}"); } catch { /* fresh */ }
  const out: Record<string, FieldState> = {};
  for (const f of fields) {
    // A record carries through as a record; text carries through as text; a
    // value that reads as neither is treated as absent, never as its own
    // stringification.
    const record = !isScalar(f.value) && describeRecord(f.value).length > 0
      ? f.value : undefined;
    const text = record === undefined && isScalar(f.value) ? String(f.value) : "";
    if (record !== undefined) {
      out[f.name] = { value: "", source: "chat", record };
    } else if (text !== "") {
      out[f.name] = { value: text, source: "chat" };
    } else if (f.name === "signatory_name" && signatory.name) {
      out[f.name] = { value: signatory.name, source: "remembered" };
    } else if (f.name === "signatory_title" && signatory.title) {
      out[f.name] = { value: signatory.title, source: "remembered" };
    } else if (TODAY_FIELDS.has(f.name)) {
      out[f.name] = { value: todayIso(), source: "user" };
    } else {
      out[f.name] = { value: "", source: "user" };
    }
  }
  return out;
}

interface FormState {
  values: Record<string, FieldState>;
  /** Optional and empty at seed time — these live behind the disclosure. The
   *  set is fixed at seed, so no field moves out of the section while it is
   *  being typed in, and a prefilled optional value stays in plain sight. */
  deferred: string[];
}

/** A field the reader has nothing to look at yet — neither typed text nor a
 *  record carried from the conversation. */
function isBlank(st: FieldState | undefined): boolean {
  return !st?.value && st?.record === undefined;
}

function seedForm(fields: LetterFormField[]): FormState {
  const values = initialFields(fields);
  return {
    values,
    deferred: fields.filter(f => !f.required && isBlank(values[f.name])).map(f => f.name),
  };
}

const SOURCE_FLAG: Record<Exclude<FieldSource, "user">, string> = {
  hr: "synced",
  chat: "from chat",
  remembered: "saved",
};

export function LetterFormCard({
  spec, busy, onSubmit,
}: {
  spec: LetterFormSpec;
  busy: boolean;
  onSubmit: (payload: LetterFormPayload, summary: string) => void;
}) {
  const [typeId, setTypeId] = useState<string>(spec.selected || spec.types[0]?.id || "");
  const type = useMemo(
    () => spec.types.find(t => t.id === typeId) || spec.types[0],
    [spec, typeId],
  );
  const [form, setForm] = useState<FormState>(() => seedForm(type?.fields || []));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [directory, setDirectory] = useState<HrDirectory | null>(null);
  const [employeeSlug, setEmployeeSlug] = useState<string>("");
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);

  // Switching letter type re-seeds the card for that type's fields, carrying
  // over any value the user already gave for a same-named field.
  const switchType = (id: string) => {
    setTypeId(id);
    const next = spec.types.find(t => t.id === id);
    if (!next) return;
    setForm(prev => {
      const seeded = seedForm(next.fields);
      for (const [name, st] of Object.entries(prev.values)) {
        if (seeded.values[name] !== undefined && !isBlank(st) && st.source !== "remembered") {
          seeded.values[name] = st;
        }
      }
      return {
        values: seeded.values,
        deferred: seeded.deferred.filter(n => isBlank(seeded.values[n])),
      };
    });
    setErrors({});
    setOptionalOpen(false);
  };

  // Auto-population (Team tier + staff surfaces): the roster loads only when
  // the engine flagged prefill; a missing /api/hr route degrades to manual
  // entry with no picker and no error.
  useEffect(() => {
    if (!spec.prefill) return;
    let alive = true;
    getHrDirectory().then(d => { if (alive && d && d.employees.length) setDirectory(d); });
    return () => { alive = false; };
  }, [spec.prefill]);

  const pickEmployee = (slug: string) => {
    setEmployeeSlug(slug);
    const emp = directory?.employees.find(e => e.slug === slug);
    if (!emp || !type) return;
    setForm(prev => {
      const values = { ...prev.values };
      for (const f of type.fields) {
        const fill = HR_PREFILL[f.name]?.(emp, directory?.company?.org_name ?? null);
        // A value the user typed (or the clerk carried from chat) wins over
        // the synced one; only empty or previously-HR-filled fields update.
        const cur = prev.values[f.name];
        if (fill && (!cur?.value || cur.source === "hr")) {
          values[f.name] = { value: fill, source: "hr" };
        }
      }
      // A synced optional value belongs in plain sight, not behind the fold.
      return { values, deferred: prev.deferred.filter(n => isBlank(values[n])) };
    });
  };

  const setValue = (name: string, value: string) => {
    setForm(prev => ({ ...prev, values: { ...prev.values, [name]: { value, source: "user" } } }));
    setErrors(prev => {
      if (!(name in prev)) return prev;
      const { [name]: _gone, ...rest } = prev;
      return rest;
    });
  };

  const submit = () => {
    if (!type) return;
    const errs: Record<string, string> = {};
    const payload: Record<string, unknown> = {};
    for (const f of type.fields) {
      const st = form.values[f.name];
      // A record goes to the pipeline exactly as the conversation gave it —
      // the composer owns the sentence it becomes.
      if (st?.record !== undefined) {
        payload[f.name] = st.record;
        continue;
      }
      const raw = (st?.value ?? "").trim();
      if (!raw) {
        if (f.required) errs[f.name] = "Required";
        continue;
      }
      if (f.kind === "number") {
        const n = Number(raw.replace(/[,$\s]/g, "").replace(/^S\$/i, ""));
        if (!Number.isFinite(n)) {
          errs[f.name] = "Enter a plain number (e.g. 4800)";
          continue;
        }
        payload[f.name] = n;
      } else {
        payload[f.name] = raw;
      }
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      // An error never hides: a flagged optional field opens the disclosure.
      if (Object.keys(errs).some(n => form.deferred.includes(n))) setOptionalOpen(true);
      return;
    }
    const sig = {
      name: String(payload.signatory_name || ""),
      title: String(payload.signatory_title || ""),
    };
    if (sig.name || sig.title) {
      try { localStorage.setItem(SIGNATORY_KEY, JSON.stringify(sig)); } catch { /* private mode */ }
    }
    onSubmit(
      { letter_type: type.id, payload },
      `Submitted the ${type.name} form.`,
    );
  };

  if (!type) return null;

  const fieldNames = new Set(type.fields.map(f => f.name));

  const groupsFor = (): Array<{ label: string; fields: LetterFormField[] }> => {
    const named = new Set(GROUPS.flatMap(g => g.fields));
    const shown = (f: LetterFormField) => !form.deferred.includes(f.name);
    return GROUPS.map(g => ({
      label: g.label,
      fields: (g.label === "Letter details"
        ? type.fields.filter(f => !named.has(f.name))
        : g.fields
          .map(name => type.fields.find(f => f.name === name))
          .filter((f): f is LetterFormField => Boolean(f))
      ).filter(shown),
    })).filter(g => g.fields.length > 0);
  };

  const deferredFields = type.fields.filter(f => form.deferred.includes(f.name));

  const roster = directory?.employees || [];
  const provenance = roster[0]?.source || directory?.company?.source || null;

  const renderField = (f: LetterFormField, inOptional: boolean) => {
    const st = form.values[f.name] || { value: "", source: "user" as const };
    const stem = STEM_RULES[f.name];
    const lint = stem && st.value && stem.bad.test(st.value) ? stem.warn : null;
    const help = FIELD_HELP[f.name];
    const hint = stem?.hint
      ?? (help && "hint" in help ? help.hint : undefined)
      ?? (f.description ? humanizeDescription(f.description, fieldNames) : "");
    const placeholder = help?.placeholder
      || (f.kind === "number" ? "e.g. 4800" : undefined);
    const long = ["incident_details", "termination_reason", "expected_correction",
      "notice_details", "final_pay_details", "benefits_info", "benefits"].includes(f.name);
    const flags: string[] = [];
    if (!f.required && !inOptional) flags.push("optional");
    if (st.source !== "user" && !isBlank(st)) flags.push(SOURCE_FLAG[st.source]);
    const hintId = `lf-hint-${f.name}`;
    const errId = `lf-err-${f.name}`;
    const describedBy = [hint ? hintId : null, errors[f.name] ? errId : null]
      .filter(Boolean).join(" ") || undefined;
    const shared = {
      value: st.value,
      placeholder,
      disabled: busy,
      "aria-describedby": describedBy,
      onFocus: () => setFocused(f.name),
      onBlur: () => setFocused(cur => (cur === f.name ? null : cur)),
      onChange: (e: { target: { value: string } }) => setValue(f.name, e.target.value),
    };
    const labelText = (
      <span className="letter-form-label">
        {labelOf(f.name)}
        {flags.length > 0 && <span className="letter-form-flag">{flags.join(" · ")}</span>}
      </span>
    );
    if (st.record !== undefined) {
      return (
        <div className="letter-form-field" key={f.name}>
          {labelText}
          <div className="letter-form-record">
            {describeRecord(st.record).map((line, i) => <div key={i}>{line}</div>)}
          </div>
          <button
            type="button"
            className="letter-form-relink"
            disabled={busy}
            onClick={() => setValue(f.name, "")}
          >
            Write this myself
          </button>
        </div>
      );
    }
    return (
      <div className={`letter-form-field${errors[f.name] ? " has-error" : ""}`} key={f.name}>
        <label>
          {labelText}
          {long
            ? <textarea rows={rowsFor(st.value)} {...shared} />
            : (
              <input
                type={isDateField(f, st.value) ? "date" : "text"}
                inputMode={f.kind === "number" ? "decimal" : undefined}
                {...shared}
              />
            )}
        </label>
        {hint && (
          <div
            id={hintId}
            className={`letter-form-hint${focused === f.name && !lint ? " is-open" : ""}`}
          >
            {hint}
          </div>
        )}
        {lint && <div className="letter-form-lint">⚠ {lint}</div>}
        {errors[f.name] && <div className="letter-form-error" id={errId}>{errors[f.name]}</div>}
      </div>
    );
  };

  return (
    <div className="letter-form-card">
      <div className="letter-form-head">
        {spec.types.length > 1 ? (
          <select
            className="letter-form-type"
            aria-label="Letter type"
            value={type.id}
            onChange={e => switchType(e.target.value)}
            disabled={busy}
          >
            {spec.types.map(t => <option key={t.id} value={t.id}>{typeLabel(t.name)}</option>)}
          </select>
        ) : (
          <span className="letter-form-title">{typeLabel(type.name)}</span>
        )}
      </div>

      {roster.length > 0 && (
        <div className="letter-form-picker">
          <label>
            <span className="letter-form-label">Employee</span>
            <select
              value={employeeSlug}
              onChange={e => pickEmployee(e.target.value)}
              disabled={busy}
            >
              <option value="">Choose an employee…</option>
              {roster.map(e => (
                <option key={e.slug} value={e.slug}>
                  {e.display_name}{e.job_title ? ` — ${e.job_title}` : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="letter-form-provenance">
            {`Prefilled from ${sourceLabel(provenance)} — edit anything, your value wins.`}
          </div>
        </div>
      )}

      {groupsFor().map(group => (
        <fieldset className="letter-form-group" key={group.label}>
          <legend>{group.label}</legend>
          {group.fields.map(f => renderField(f, false))}
        </fieldset>
      ))}

      {deferredFields.length > 0 && (
        <div className="letter-form-optional">
          <button
            type="button"
            className="letter-form-disclosure"
            aria-expanded={optionalOpen}
            onClick={() => setOptionalOpen(o => !o)}
            disabled={busy}
          >
            <span className="letter-form-caret" aria-hidden="true">{optionalOpen ? "▾" : "▸"}</span>
            Optional details ({deferredFields.length})
          </button>
          {optionalOpen && (
            <div className="letter-form-optional-body">
              <p className="letter-form-optional-note">
                Leave any of these blank and the letter uses its standard wording.
              </p>
              {deferredFields.map(f => renderField(f, true))}
            </div>
          )}
        </div>
      )}

      <div className="letter-form-actions">
        <button className="primary" onClick={submit} disabled={busy}>
          {busy ? "…" : "Generate letter"}
        </button>
      </div>
    </div>
  );
}
